import React, { FC, useCallback, useEffect, useState } from "react";

import cn from "classnames";
import { isSameYear } from "date-fns";
import { startOfDay } from "date-fns/esm";
import { PanelMode } from "rc-picker/lib/interface";
import { useIntl } from "react-intl";

import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import { DatePicker } from "@mapmycustomers/ui";

import { formatDate } from "@app/util/formatters";
import useStateWithTransformer from "@app/util/hook/useStateWithTransformer";
import isValidDate from "@app/util/isValidDate";
import { parseApiDate } from "@app/util/parsers";

import EditorComponentProps from "./EditorComponentProps";
import styles from "./OrdinalDatesEditor.module.scss";

type MaybeDate = Date | undefined;

const parseAndNormalizeDate = (value: string | undefined): MaybeDate => {
  if (!value) {
    return undefined;
  }

  const date = parseApiDate(`${new Date().getFullYear()}-${value}`);
  if (!isValidDate(date)) {
    return undefined;
  }

  return startOfDay(date);
};

const useDateState = (initialState: (() => MaybeDate) | MaybeDate) =>
  useStateWithTransformer<MaybeDate>(initialState, (date: MaybeDate) =>
    date ? startOfDay(date) : undefined
  );

export interface OrdinalDatesEditorProps extends EditorComponentProps<string> {}

const OrdinalDatesEditor: FC<OrdinalDatesEditorProps> = ({
  className,
  duplicate,
  onChange,
  value,
  ...props
}) => {
  const intl = useIntl();

  const [internalValue, setInternalValue] = useDateState(() => parseAndNormalizeDate(value));
  useEffect(() => setInternalValue(parseAndNormalizeDate(value)), [setInternalValue, value]);

  const duplicateError =
    internalValue !== undefined && internalValue.toISOString() !== value && !!duplicate;

  // wrapping onChange in dynamic callback to prevent triggering useEffect
  // because of the onChange changes
  const handleChange = useDynamicCallback((value: string) => onChange?.(value));
  useEffect(() => {
    if (internalValue === undefined) {
      return;
    }
    handleChange(formatDate(internalValue, "MM-dd"));
  }, [duplicateError, internalValue, handleChange]);

  const [mode, setMode] = useState<PanelMode>("month");
  const [open, setOpen] = useState(false);
  const handleOpenChange = useCallback((open: boolean) => {
    if (open) {
      setMode("month");
    }
    setOpen(open);
  }, []);

  return (
    <DatePicker
      className={cn(styles.container, className)}
      disabledDate={(date) => !isSameYear(date, Date.now())}
      dropdownClassName={styles.dropdown}
      error={duplicateError ? "Break point already exists" : ""}
      format={intl.formatMessage({
        id: "map.colorization.createEditSidebar.styleEditor.datingMethod.ordinal.format",
        defaultMessage: "MMMM dd",
        description: "Date format for the Ordinal Dating method in Pin Legends",
      })}
      mode={mode}
      onChange={setInternalValue}
      onOpenChange={handleOpenChange}
      onPanelChange={(_, mode: PanelMode) => setMode(mode)}
      open={open}
      value={internalValue}
      {...props}
    />
  );
};

export default OrdinalDatesEditor;
